import dayjs from 'dayjs'
import {SorterResult} from 'antd/es/table/interface'
import {EventReportKeyColumnDescription, EventReportValueColumnDescription, KeyFilterClause, SavedReportBean} from 'src/api/api'
import {Filter} from './Filters'

const startFormat = 'YYYY-MM-DD 00:00:00'
const endFormat = 'YYYY-MM-DD 23:00:00'
const MIN_ALL_TIME_START = '2000-01-01 00:00:00'

const KeyColumns = EventReportKeyColumnDescription.ColumnEnum
const OrderEnum = EventReportKeyColumnDescription.OrderEnum

type DatesRangeFn = (now: dayjs.Dayjs) => [string, string]
type DatesMap = Partial<Record<SavedReportBean.ReportPeriodEnum, DatesRangeFn>>

const datesMap: DatesMap = {
    [SavedReportBean.ReportPeriodEnum.TODAY]: (now) => [now.format(startFormat), now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.YESTERDAY]: (now) => {
        const y = now.subtract(1, 'day')
        return [y.format(startFormat), y.format(endFormat)]
    },
    [SavedReportBean.ReportPeriodEnum.LAST3DAYS]: (now) => [now.subtract(3, 'day').format(startFormat), now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.LAST7DAYS]: (now) => [now.subtract(7, 'day').format(startFormat), now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.LAST30DAYS]: (now) => [now.subtract(30, 'day').format(startFormat), now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.THISMONTH]: (now) => [now.startOf('month').format(startFormat), now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.LASTMONTH]: (now) => [
        now.subtract(1, 'month').startOf('month').format(startFormat),
        now.subtract(1, 'month').endOf('month').format(endFormat),
    ],
    [SavedReportBean.ReportPeriodEnum.ALLTIME]: (now) => [MIN_ALL_TIME_START, now.format(endFormat)],
    [SavedReportBean.ReportPeriodEnum.CUSTOMRANGE]: undefined,
}

const handleFiltersToKeyColumns = (filters: Filter[], res: EventReportKeyColumnDescription[]) => {
    filters.forEach((filter) => {
        if (!filter.type || !filter.clauseType || !filter.values || filter.values.length === 0) return
        res.push({
            onlyFilter: true,
            column: filter.type,
            where: {
                type: filter.clauseType,
                value: filter.values,
            },
        })
    })
}

type BuildReportColumnsParams = {
    formValues: any
    allColumnsInOrder: string[]
    filters: Filter[]
    sorter?: SorterResult<any>
    now: dayjs.Dayjs
    isKeyColumn: (col: string) => boolean
    isValueColumn: (col: string) => boolean
}

export const buildReportColumns = (params: BuildReportColumnsParams) => {
    const {formValues, allColumnsInOrder, filters, sorter, now, isKeyColumn, isValueColumn} = params

    const reportPeriod: SavedReportBean.ReportPeriodEnum | undefined = formValues.period

    const buildDateCol = (onlyFilter?: boolean): EventReportKeyColumnDescription => {
        const date: EventReportKeyColumnDescription = {
            column: KeyColumns.DayHour,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value:
                    reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE
                        ? [formValues.startDate?.format(startFormat), formValues.endDate?.format(endFormat)]
                        : reportPeriod
                            ? datesMap[reportPeriod]?.(now)
                            : undefined,
            },
        }

        if (onlyFilter) date.onlyFilter = true
        if (!sorter) date.order = OrderEnum.Asc

        return date
    }

    const getKeyColumns = (): EventReportKeyColumnDescription[] => {
        const res: EventReportKeyColumnDescription[] = []

        const dayHourSelected = allColumnsInOrder.includes(KeyColumns.DayHour as any)

        allColumnsInOrder.forEach((col) => {
            if (!isKeyColumn(col)) return

            if (col === (KeyColumns.DayHour as any)) {
                res.push(buildDateCol(false))
                return
            }

            res.push({column: col as any})
        })

        if (!dayHourSelected) {
            res.push(buildDateCol(true))
        }

        handleFiltersToKeyColumns(filters, res)
        return res
    }

    const getValueColumns = (): EventReportValueColumnDescription[] => {
        const res: EventReportValueColumnDescription[] = []
        if (!allColumnsInOrder) return res

        allColumnsInOrder.forEach((col) => {
            if (!isValueColumn(col)) return
            res.push({column: col as any})
        })

        return res
    }

    const keyColumns = getKeyColumns()
    const valueColumns = getValueColumns()

    if (sorter && sorter.column && sorter.order) {
        const column = String((sorter.column.title as any) ?? '').replace(/\s+/g, '')
        const order = sorter.order == 'ascend' ? OrderEnum.Asc : OrderEnum.Desc

        const byKey = keyColumns.some((k) => {
            if (String(k.column) === column) {
                k.order = order
                return true
            }
            return false
        })

        if (!byKey) {
            valueColumns.some((v) => {
                if (String(v.column) === column) {
                    ;(v as any).order = order
                    return true
                }
                return false
            })
        }
    }

    return {keyColumns, valueColumns}
}


// WEBPACK FOOTER //
// ./src/component/app/report/reportRequest.ts